import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconBell,
  IconCheck,
  IconExternalLink,
  IconLoader2,
} from '@tabler/icons-react';
import axios from '../../../library/axios';
import { MySwal } from '../../../library/swal';
import CpanelPageShell from '../../../components/cpanel/CpanelPageShell';
import {
  NOTIFICATION_TABS,
  formatNotificationDateTime,
  formatNotificationTime,
  isValidNotificationTab,
} from '../../../helpers/cpanelNotifications';
import { CPANEL_FEED_REFRESH_EVENT } from '../../../helpers/cpanelJobFeedback';

const DEFAULT_LIMIT = 15;

const CpanelNotificationsPage = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const activeTab = isValidNotificationTab(tabParam) ? tabParam : 'all';

  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: DEFAULT_LIMIT,
    totalPages: 1,
  });

  const fetchNotifications = useCallback(
    async (page = 1, limit = DEFAULT_LIMIT, options = {}) => {
      const { silent = false } = options;
      if (!silent) {
        setLoading(true);
      }
      try {
        const { data } = await axios.get('/cpanel/notifications/list', {
          params: { tab: activeTab, page, limit },
        });
        setUnreadCount(data?.unreadCount ?? 0);
        setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
        setPaginationMeta((prev) => data?.meta || { ...prev, total: 0, page: 1, limit, totalPages: 1 });
      } catch (err) {
        if (!silent) {
          setUnreadCount(0);
          setNotifications([]);
          MySwal.fire({
            icon: 'error',
            title: 'Gagal memuat notifikasi',
            text: err?.response?.data?.message || 'Tidak dapat mengambil daftar notifikasi.',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab]
  );

  useEffect(() => {
    if (tabParam && !isValidNotificationTab(tabParam)) {
      navigate('/cpanel/notifications/all', { replace: true });
      return;
    }
    fetchNotifications(1, paginationMeta.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabParam, navigate]);

  useEffect(() => {
    const onRefresh = () =>
      fetchNotifications(paginationMeta.page, paginationMeta.limit, { silent: true });
    window.addEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationMeta.page, paginationMeta.limit, activeTab]);

  const handleTabChange = (tabId) => {
    navigate(`/cpanel/notifications/${tabId}`);
  };

  const handleChangePage = (page) => {
    fetchNotifications(page, paginationMeta.limit);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch('/cpanel/notifications/mark-all-read');
      await fetchNotifications(paginationMeta.page, paginationMeta.limit);
      MySwal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Semua notifikasi ditandai sudah dibaca.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal',
        text: err?.response?.data?.message || 'Tidak dapat menandai notifikasi.',
      });
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`/cpanel/notifications/${id}/mark-read`);
      await fetchNotifications(paginationMeta.page, paginationMeta.limit);
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal',
        text: err?.response?.data?.message || 'Tidak dapat menandai notifikasi.',
      });
    }
  };

  const handleOpen = async (item) => {
    if (!item.isRead) {
      try {
        await axios.patch(`/cpanel/notifications/${item.id}/mark-read`);
      } catch {
        // continue navigation even if mark-read fails
      }
    }
    if (item.url) {
      navigate(item.url);
    }
  };

  const pageActions = unreadCount > 0 ? (
    <button type="button" className="btn btn-sm btn-primary" onClick={handleMarkAllRead}>
      <IconCheck size={16} stroke={2} className="me-1" />
      Tandai semua dibaca
    </button>
  ) : null;

  const { page, totalPages, total } = paginationMeta;

  return (
    <CpanelPageShell
      title="Notifikasi"
      subtitle="Pemberitahuan aktivitas dan job CPanel"
      actions={pageActions}
      breadcrumbs={[
        { label: 'CPanel', to: '/cpanel/dashboard' },
        { label: 'Activity' },
        { label: 'Notifikasi' },
      ]}
    >
      <div className="card cpanel-card cpanel-notifications-page">
        <div className="card-body">
          <div className="cpanel-notifications-page__tabs" role="tablist">
            {NOTIFICATION_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`cpanel-notifications-page__tab${activeTab === tab.id ? ' cpanel-notifications-page__tab--active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
                {tab.id === 'unread' && unreadCount > 0 ? (
                  <span className="cpanel-notifications-page__tab-badge">{unreadCount}</span>
                ) : null}
              </button>
            ))}
          </div>

          {loading && notifications.length === 0 ? (
            <div className="cpanel-notifications-page__empty">Memuat notifikasi...</div>
          ) : null}

          {!loading && notifications.length === 0 ? (
            <div className="cpanel-notifications-page__empty">
              <IconBell size={40} stroke={1.25} className="cpanel-notifications-page__empty-icon" />
              <p>Tidak ada notifikasi</p>
            </div>
          ) : null}

          {notifications.length > 0 ? (
            <div className="cpanel-notifications-page__list">
              {notifications.map((item) => {
                const inProgress =
                  item.type === 'job.started' || item.data?.status === 'running';
                const iconVariant =
                  item.data?.icon === 'danger'
                    ? 'danger'
                    : item.data?.icon === 'progress' || inProgress
                      ? 'progress'
                      : 'success';

                return (
                <div
                  key={item.id}
                  className={`cpanel-notifications-page__item${!item.isRead ? ' cpanel-notifications-page__item--unread' : ''}${inProgress ? ' cpanel-notifications-page__item--progress' : ''}`}
                >
                  <div className="cpanel-notifications-page__item-main">
                    <span
                      className={`cpanel-notifications__item-icon cpanel-notifications__item-icon--${iconVariant}`}
                      aria-hidden="true"
                    >
                      {iconVariant === 'progress' ? (
                        <IconLoader2 size={18} stroke={1.75} className="cpanel-notifications__spin" />
                      ) : (
                        <IconBell size={18} stroke={1.75} />
                      )}
                    </span>
                    <div className="cpanel-notifications-page__item-body">
                      <div className="cpanel-notifications-page__item-title">{item.title}</div>
                      {item.body ? (
                        <div
                          className={`cpanel-notifications-page__item-text${item.data?.icon === 'danger' ? ' cpanel-notifications-page__item-text--error' : ''}`}
                        >
                          {item.body}
                        </div>
                      ) : null}
                      <div className="cpanel-notifications-page__item-meta">
                        <span title={formatNotificationDateTime(item.createdAt)}>
                          {formatNotificationTime(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="cpanel-notifications-page__item-actions">
                    {!item.isRead ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={() => handleMarkRead(item.id)}
                      >
                        <IconCheck size={16} stroke={2} />
                        <span className="d-none d-md-inline ms-1">Dibaca</span>
                      </button>
                    ) : null}
                    {item.url ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => handleOpen(item)}
                      >
                        <IconExternalLink size={16} stroke={2} />
                        <span className="d-none d-md-inline ms-1">Buka</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              );
              })}
            </div>
          ) : null}

          {totalPages > 1 ? (
            <div className="cpanel-notifications-page__pagination">
              <span className="text-muted small">
                {total} notifikasi - halaman {page} dari {totalPages}
              </span>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={page <= 1 || loading}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Sebelumnya
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={page >= totalPages || loading}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Berikutnya
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </CpanelPageShell>
  );
};

export default CpanelNotificationsPage;
